const INTERCEPTING_ROUTE_SEGMENT_PATTERN = /^\(\.+\)/u;
// A Next.js dynamic segment folder name: `[handle]`, `[...slug]`, `[[...slug]]`.
const DYNAMIC_ROUTE_SEGMENT_PATTERN = /^\[{1,2}(?:\.\.\.)?[^\]]+\]{1,2}$/u;

export function isDynamicRouteSegment(segment: string): boolean {
  return DYNAMIC_ROUTE_SEGMENT_PATTERN.test(segment);
}

/** Whether `route` is a template that serves many URLs, such as `/products/[slug]`. */
export function isDynamicRoute(route: string): boolean {
  return route.split("/").some(isDynamicRouteSegment);
}

export const SITE_PAGE_FILE_NAMES = {
  legacy: "page.tsx",
  draft: "page.dev.tsx",
  live: "page.published.tsx",
} as const;

export type SitePageFileKind = keyof typeof SITE_PAGE_FILE_NAMES;

// The publish build routes by this bare extension, and Next applies
// pageExtensions to every app-router special file (layout, error, …), not
// just page — so guards against stray copies must scope to the extension,
// never to the page.published.tsx basename alone.
export const SITE_PAGE_PUBLISHED_FILE_EXTENSION = ".published.tsx";

const SITE_PAGE_FILE_KINDS = [
  "legacy",
  "draft",
  "live",
] as const satisfies readonly SitePageFileKind[];

export const SITE_PAGE_FILE_GLOBS = Object.values(SITE_PAGE_FILE_NAMES).map(
  (name) => `app/**/${name}`,
);

export const SITE_PAGE_PUBLISH_STATES = [
  "published",
  "publishedWithChanges",
  "unpublished",
  "pendingDelete",
  "legacy",
] as const;

export type SitePagePublishState = (typeof SITE_PAGE_PUBLISH_STATES)[number];

export function getSitePageFileKind(filepath: string): SitePageFileKind | null {
  const fileName = filepath.split("/").at(-1);
  return (
    SITE_PAGE_FILE_KINDS.find(
      (kind) => SITE_PAGE_FILE_NAMES[kind] === fileName,
    ) ?? null
  );
}

export function isSupportedSitePageFilepath(filepath: string): boolean {
  const pathSegments = filepath.split("/").filter(Boolean);
  if (getSitePageFileKind(filepath) === null) {
    return false;
  }

  const appDirectoryIndex = pathSegments.indexOf("app");
  if (
    appDirectoryIndex === -1 ||
    pathSegments[appDirectoryIndex - 1] === "src"
  ) {
    return false;
  }

  const routeSegments = pathSegments.slice(appDirectoryIndex + 1, -1);
  return !routeSegments.some((segment) => {
    return (
      segment === "sections" ||
      segment.startsWith("@") ||
      segment.startsWith("_") ||
      INTERCEPTING_ROUTE_SEGMENT_PATTERN.test(segment)
    );
  });
}

export function deriveSitePagePublishState({
  kinds,
  draftMatchesLive,
}: {
  kinds: ReadonlySet<SitePageFileKind>;
  draftMatchesLive: boolean | null;
}): SitePagePublishState | "invalid" {
  if (kinds.has("legacy")) {
    return kinds.size === 1 ? "legacy" : "invalid";
  }
  if (kinds.has("draft") && kinds.has("live")) {
    return draftMatchesLive ? "published" : "publishedWithChanges";
  }
  if (kinds.has("draft")) {
    return "unpublished";
  }
  return "pendingDelete";
}

// Keying by directory keeps route-group pages distinct when they serve the same route.
export function groupSitePageFiles<
  PageFileType extends {
    filepath: string;
    route: string;
    contentHash?: string;
  },
>(
  files: PageFileType[],
): (PageFileType & { publishState: SitePagePublishState })[] {
  const filesByDirectory = new Map<
    string,
    {
      fallbackFile: PageFileType;
      filesByKind: Partial<Record<SitePageFileKind, PageFileType>>;
    }
  >();
  for (const file of files) {
    const kind = getSitePageFileKind(file.filepath);
    if (kind === null) {
      continue;
    }
    const directory = file.filepath.slice(0, file.filepath.lastIndexOf("/"));
    const existingFiles = filesByDirectory.get(directory);
    filesByDirectory.set(directory, {
      fallbackFile: existingFiles?.fallbackFile ?? file,
      filesByKind: {
        ...existingFiles?.filesByKind,
        [kind]: file,
      },
    });
  }

  return [...filesByDirectory.values()].map(({ fallbackFile, filesByKind }) => {
    const draftMatchesLive =
      filesByKind.draft?.contentHash !== undefined &&
      filesByKind.live?.contentHash !== undefined
        ? filesByKind.draft.contentHash === filesByKind.live.contentHash
        : null;
    const kinds = new Set<SitePageFileKind>();
    for (const kind of SITE_PAGE_FILE_KINDS) {
      if (filesByKind[kind]) {
        kinds.add(kind);
      }
    }
    const state = deriveSitePagePublishState({
      kinds,
      draftMatchesLive,
    });
    const editableFile =
      filesByKind.draft ??
      filesByKind.legacy ??
      filesByKind.live ??
      fallbackFile;
    return {
      ...editableFile,
      publishState: state === "invalid" ? "publishedWithChanges" : state,
    };
  });
}
